import Phaser from 'phaser';
import { CONFIG } from '../config.js';
import { getAudio } from '../systems/AudioSystem.js';
import { SaveSystem } from '../systems/SaveSystem.js';

const C = CONFIG.COLORS;

export class HUDScene extends Phaser.Scene {
  constructor() {
    super({ key: 'HUDScene' });
  }

  init(data) {
    this.gameScene = data.gameScene;
  }

  create() {
    const W = CONFIG.GAME_WIDTH;
    const HEADER_Y = 28;

    // HP bar (left)
    this.hpLabel = this.add
      .text(8, HEADER_Y - 6, 'HP', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#ef4444',
      })
      .setOrigin(0, 0.5);

    this.hpBarBg = this.add
      .rectangle(28, HEADER_Y - 6, 110, 12, 0x312e81, 0.85)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, 0xffffff, 0.6);
    this.hpBar = this.add
      .rectangle(28, HEADER_Y - 6, 110, 12, C.HP_GREEN, 1)
      .setOrigin(0, 0.5);
    this.hpText = this.add
      .text(28 + 110 + 4, HEADER_Y - 6, '100', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#ffffff',
      })
      .setOrigin(0, 0.5);

    // Wave (center)
    this.waveText = this.add
      .text(W / 2, HEADER_Y - 14, 'WAVE 1/3', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '10px',
        color: '#fde047',
      })
      .setOrigin(0.5, 0);
    this.waveSubText = this.add
      .text(W / 2, HEADER_Y, '0/20', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '7px',
        color: '#9ca3af',
      })
      .setOrigin(0.5, 0);

    // Score (right)
    this.scoreText = this.add
      .text(W - 8, HEADER_Y - 16, 'SCORE 0', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '9px',
        color: '#06b6d4',
      })
      .setOrigin(1, 0);
    this.comboText = this.add
      .text(W - 8, HEADER_Y - 4, '', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#fbbf24',
      })
      .setOrigin(1, 0);

    // Audio toggle pill (top-right)
    const audio = getAudio();
    this.audioBtn = this.add
      .text(W - 8, HEADER_Y + 10, audio.enabled ? 'AUDIO ON' : 'AUDIO OFF', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '7px',
        color: audio.enabled ? '#22c55e' : '#9ca3af',
        backgroundColor: '#0f0d2c',
        padding: { x: 4, y: 3 },
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    this.audioBtn.on('pointerup', () => {
      const enabled = audio.toggle();
      SaveSystem.setAudioEnabled(enabled);
      this.audioBtn.setText(enabled ? 'AUDIO ON' : 'AUDIO OFF');
      this.audioBtn.setColor(enabled ? '#22c55e' : '#9ca3af');
    });

    // Wave subscriptions
    this.gameScene.events.on('hud-update', this.refresh, this);
    this.gameScene.events.on('wave-start', this.refresh, this);
    this.gameScene.events.on('wave-end', this.refresh, this);
    this.gameScene.events.on('wave-boss-start', () => {
      this.waveText.setText('FINAL BOSS');
      this.waveText.setColor('#ef4444');
      const bossCfg = (CONFIG.BOSSES && CONFIG.BOSSES[this.gameScene.level]) || CONFIG.BOSSES.basic;
      this.waveSubText.setText(bossCfg.subtitle || 'BOSS');
    });
    this.gameScene.events.on('wave-progress', ({ kills, target }) => {
      this.waveSubText.setText(`${kills}/${target}`);
    });
    this.gameScene.events.on('shutdown', () => {
      this.scene.stop();
    });

    this.refresh();
  }

  refresh() {
    const gs = this.gameScene;
    if (!gs || !gs.player) return;
    const ratio = Math.max(0, gs.player.hp / gs.player.maxHp);
    this.hpBar.width = 110 * ratio;
    this.hpBar.fillColor =
      ratio > 0.5 ? C.HP_GREEN : ratio > 0.25 ? C.HP_YELLOW : C.HP_RED;
    this.hpText.setText(`${gs.player.hp}`);

    const ws = gs.waveSystem;
    if (ws.state === 'boss') {
      this.waveText.setText('FINAL BOSS');
      this.waveText.setColor('#ef4444');
      const boss = gs.boss || gs.enemies.getChildren().find((e) => e.isBoss);
      if (boss) this.waveSubText.setText(`HP ${boss.hp}/${boss.maxHp}`);
    } else if (ws.state === 'between') {
      this.waveText.setText('READY...');
      this.waveText.setColor('#fbbf24');
      this.waveSubText.setText('');
    } else if (ws.currentWaveIndex >= 0) {
      const idx = ws.currentWaveIndex;
      const waves = ws.waves || CONFIG.WAVES;
      const wave = waves[idx];
      this.waveText.setText(`WAVE ${wave.id}/${waves.length}`);
      this.waveText.setColor('#fde047');
      this.waveSubText.setText(`${ws.killsThisWave}/${wave.killTarget}`);
    }

    this.scoreText.setText(`SCORE ${gs.runStats.score}`);
    if (gs.combo >= 2) {
      this.comboText.setText(`COMBO x${gs.combo}`);
    } else {
      this.comboText.setText('');
    }
  }

  update() {
    if (!this.gameScene || !this.gameScene.scene.isActive()) return;
    // Cheap per-frame refresh for boss HP / wave timer
    if (this.gameScene.waveSystem && this.gameScene.waveSystem.state === 'boss') {
      const boss = this.gameScene.boss || this.gameScene.enemies.getChildren().find((e) => e.isBoss);
      if (boss && boss.active) this.waveSubText.setText(`HP ${boss.hp}/${boss.maxHp}`);
    }
  }
}
